import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  MessageCircle,
  Target,
  DollarSign,
  TrendingUp,
  CheckCircle2,
  Clock,
} from "lucide-react";

const API = "/api";
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const formatCurrency = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const maskPhone = (phone) => {
  if (!phone) return "—";
  const digits = String(phone).replace(/\D/g, "");
  if (digits.length < 4) return phone;
  return `••••${digits.slice(-4)}`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function CampaignReportModal({ campaignId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/campaigns/${campaignId}/events?limit=50`,
          getAuthHeader()
        );
        if (!active) return;
        setCampaign(res.data.campaign);
        setEvents(Array.isArray(res.data.events) ? res.data.events : []);
      } catch {
        if (active) toast.error("Erro ao carregar relatório");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [campaignId]);

  const triggered = useMemo(
    () => events.filter((e) => e.event_type === "triggered"),
    [events]
  );
  const converted = useMemo(
    () => events.filter((e) => e.event_type === "converted"),
    [events]
  );

  const triggeredCount = Number(campaign?.triggered_count || triggered.length);
  const convertedCount = Number(campaign?.converted_count || converted.length);
  const revenue = Number(
    campaign?.recovered_revenue ||
      converted.reduce((acc, e) => acc + Number(e.order_value || 0), 0)
  );
  const rate =
    triggeredCount > 0
      ? ((convertedCount / triggeredCount) * 100).toFixed(1)
      : "0.0";

  // Mini chart últimos 7 dias
  const chart = useMemo(() => {
    const now = new Date();
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const key = d.toISOString().slice(0, 10);
      days.push({ key, label: d.getDate().toString().padStart(2, "0"), count: 0 });
    }
    const idx = Object.fromEntries(days.map((d, i) => [d.key, i]));
    triggered.forEach((e) => {
      if (!e.created_at) return;
      const key = e.created_at.slice(0, 10);
      if (key in idx) days[idx[key]].count += 1;
    });
    const max = Math.max(1, ...days.map((d) => d.count));
    return { days, max };
  }, [triggered]);

  // Mapa de triggered -> converted by phone (dentro de 2h)
  const convertedPhones = useMemo(() => {
    const set = new Set();
    converted.forEach((e) => {
      if (e.phone) set.add(`${e.phone}|${e.created_at?.slice(0, 16) || ""}`);
    });
    return set;
  }, [converted]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-2xl border shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-card z-10 rounded-t-2xl">
          <h2 className="text-lg font-bold">
            Relatório — {campaign?.name || "Campanha"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <>
              {/* Métricas */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <StatCard
                  icon={MessageCircle}
                  iconColor="text-blue-500"
                  label="Disparos"
                  value={triggeredCount}
                />
                <StatCard
                  icon={Target}
                  iconColor="text-green-500"
                  label="Conversões"
                  value={convertedCount}
                />
                <StatCard
                  icon={DollarSign}
                  iconColor="text-emerald-500"
                  label="Receita recuperada"
                  value={formatCurrency(revenue)}
                />
                <StatCard
                  icon={TrendingUp}
                  iconColor="text-purple-500"
                  label="Taxa de conversão"
                  value={`${rate}%`}
                />
              </div>

              {/* Gráfico */}
              <div className="bg-muted/20 rounded-xl p-4 mb-6">
                <p className="text-xs font-semibold text-muted-foreground mb-3">
                  Disparos nos últimos 7 dias
                </p>
                <div className="flex items-end gap-2 h-24">
                  {chart.days.map((d) => (
                    <div
                      key={d.key}
                      className="flex-1 flex flex-col items-center gap-1"
                    >
                      <div
                        className="w-full bg-purple-500/80 rounded-t-md"
                        style={{
                          height: `${(d.count / chart.max) * 100}%`,
                          minHeight: d.count > 0 ? "4px" : "0",
                        }}
                        title={`${d.count} disparos`}
                      />
                      <span className="text-[10px] text-muted-foreground">
                        {d.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Lista de disparos */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-2">
                  Últimos disparos
                </p>
                {triggered.length === 0 ? (
                  <div className="text-center py-8 border border-dashed rounded-lg">
                    <Clock className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Nenhum disparo registrado ainda.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {triggered.map((ev) => {
                      const convEvent = converted.find(
                        (c) =>
                          c.phone === ev.phone &&
                          c.created_at > ev.created_at
                      );
                      return (
                        <div
                          key={ev.id}
                          className="flex items-start gap-3 bg-muted/30 rounded-lg px-3 py-2"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-mono">
                                {maskPhone(ev.phone)}
                              </span>
                              <span className="text-[10px] text-muted-foreground">
                                {formatDate(ev.created_at)}
                              </span>
                              {convEvent ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-500/15 text-green-600 dark:text-green-400">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Converteu
                                  {convEvent.order_value
                                    ? ` · ${formatCurrency(convEvent.order_value)}`
                                    : ""}
                                </span>
                              ) : (
                                <span className="text-[10px] text-muted-foreground">
                                  Sem conversão
                                </span>
                              )}
                            </div>
                            {ev.message_text && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                “{ev.message_text}”
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, iconColor, label, value }) {
  return (
    <div className="bg-muted/30 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <span className="text-[11px] text-muted-foreground uppercase tracking-wide">
          {label}
        </span>
      </div>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}
